// src/features/sheets/hooks/useSpreadsheet.ts
// Core hook for spreadsheet functionality

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import type { CellPosition, CellFormat, FillDragState, Sheet } from "../types";
import { DEFAULT_COL_WIDTH, MIN_COL_WIDTH } from "../types";
import {
  evaluateFormula,
  colIndexToLetter,
  extractLiveFormulas,
  containsLiveFormula,
  type CellGetter,
} from "../utils/formulaEngine";
import { fetchLiveValue } from "../utils/liveDataService";

// ─────────────────────────────────────────────────────────────────────────────
// Fill Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adjusts cell references in a formula when filling down/right
 * E.g., =A1+B1 becomes =A2+B2 when filling down by 1 row
 */
function adjustFormulaCellRefs(
  formula: string,
  rowDelta: number,
  colDelta: number
): string {
  const cellRefRegex = /(\$?)([A-Z]+)(\$?)(\d+)/g;

  return formula.replace(cellRefRegex, (_match, colAbs, colLetters, rowAbs, rowNum) => {
    let newCol = colLetters;
    if (!colAbs) {
      const colIndex = colLetters.split("").reduce((acc: number, char: string) => {
        return acc * 26 + (char.charCodeAt(0) - 64);
      }, 0) - 1;
      const newColIndex = Math.max(0, colIndex + colDelta);
      newCol = colIndexToLetter(newColIndex);
    }

    let newRow = parseInt(rowNum, 10);
    if (!rowAbs) {
      newRow = Math.max(1, newRow + rowDelta);
    }

    return `${colAbs}${newCol}${rowAbs}${newRow}`;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Format Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculate the number of decimal places needed to show at least `minSigFigs` significant figures.
 * For numbers >= 1, returns the base decimals.
 * For small numbers like 0.0012, returns enough decimals to show significant digits.
 */
function getDecimalsForSignificantFigures(
  value: number,
  baseDecimals: number,
  minSigFigs: number = 2
): number {
  if (value === 0) return baseDecimals;

  const absVal = Math.abs(value);

  // For numbers >= 1, the base decimals are sufficient
  if (absVal >= 1) return baseDecimals;

  // For small numbers, calculate how many decimals needed for significant figures
  // E.g., 0.0012 -> need 4 decimals to show "0.0012" (2 sig figs)
  // E.g., 0.00034 -> need 5 decimals to show "0.00034" (2 sig figs)
  const magnitude = Math.floor(Math.log10(absVal)); // e.g., 0.0012 -> -3
  const neededDecimals = -magnitude + (minSigFigs - 1); // e.g., 3 + 1 = 4

  return Math.max(baseDecimals, neededDecimals);
}

/**
 * Format a number with at least 2 significant figures
 */
function formatWithSignificantFigures(
  value: number,
  baseDecimals: number,
  options?: Intl.NumberFormatOptions
): string {
  const decimals = getDecimalsForSignificantFigures(value, baseDecimals);
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
    ...options,
  });
}

function formatDisplayValue(value: string | number, format?: CellFormat): string {
  if (typeof value === "string" && value.startsWith("#")) {
    return value; // Error
  }

  // Check if value is actually a number (not just starts with digits)
  // parseFloat("7D Change") returns 7, but we want to treat it as text
  const strValue = String(value);
  const numVal = typeof value === "number" ? value : parseFloat(strValue);

  // Return as text if:
  // 1. parseFloat returns NaN, OR
  // 2. The string isn't purely numeric (has non-numeric chars after the number)
  if (typeof value === "string") {
    const trimmed = strValue.trim();
    // Check if string is a valid number format (including negatives, decimals)
    const isValidNumber = /^-?\d*\.?\d+$/.test(trimmed);
    if (!isValidNumber) {
      return value; // Text - return as-is
    }
  }

  if (isNaN(numVal)) {
    return String(value); // Fallback
  }

  const baseDecimals = format?.decimals ?? 2;

  switch (format?.format) {
    case "currency": {
      const decimals = getDecimalsForSignificantFigures(numVal, baseDecimals);
      return numVal.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: Math.min(decimals, 2), // Currency typically shows at least 2
        maximumFractionDigits: decimals,
      });
    }
    case "percent": {
      // API returns decimals (0.07 for 7%), multiply by 100 for display
      const displayVal = numVal * 100;
      const decimals = getDecimalsForSignificantFigures(displayVal, baseDecimals);
      return (
        displayVal.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals,
        }) + "%"
      );
    }
    case "compact": {
      let scaledVal: number;
      let suffix: string;

      if (Math.abs(numVal) >= 1e12) {
        scaledVal = numVal / 1e12;
        suffix = "T";
      } else if (Math.abs(numVal) >= 1e9) {
        scaledVal = numVal / 1e9;
        suffix = "B";
      } else if (Math.abs(numVal) >= 1e6) {
        scaledVal = numVal / 1e6;
        suffix = "M";
      } else if (Math.abs(numVal) >= 1e3) {
        scaledVal = numVal / 1e3;
        suffix = "K";
      } else {
        scaledVal = numVal;
        suffix = "";
      }

      const decimals = getDecimalsForSignificantFigures(scaledVal, baseDecimals);
      // Add $ prefix for large USD values (market cap, TVL, fees, etc.)
      return "$" + scaledVal.toFixed(decimals) + suffix;
    }
    case "number":
      return formatWithSignificantFigures(numVal, baseDecimals);
    default:
      if (!isNaN(numVal)) {
        return formatWithSignificantFigures(numVal, baseDecimals);
      }
      return String(value);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Options
// ─────────────────────────────────────────────────────────────────────────────

export interface UseSpreadsheetOptions {
  sheet: Sheet;
  onDataChange?: ((data: string[][]) => void) | undefined;
  onFormatsChange?: ((formats: Record<string, CellFormat>) => void) | undefined;
  onColWidthsChange?: ((colWidths: number[]) => void) | undefined;
  readOnly?: boolean | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useSpreadsheet(options: UseSpreadsheetOptions) {
  const { sheet, onDataChange, onFormatsChange, onColWidthsChange, readOnly = false } = options;

  // Local state for immediate UI updates
  const [data, setData] = useState<string[][]>(sheet.data);
  const [formats, setFormats] = useState<Record<string, CellFormat>>(sheet.formats);
  const [colWidths, setColWidths] = useState<number[]>(sheet.colWidths);

  // Selection state
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [editValue, setEditValue] = useState("");

  // Live data state
  const [liveCache, setLiveCache] = useState<Map<string, number | null>>(new Map());
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Fill drag state
  const [fillDrag, setFillDrag] = useState<FillDragState | null>(null);
  const [fillPreviewCells, setFillPreviewCells] = useState<CellPosition[]>([]);

  // Column resize state
  const [resizingCol, setResizingCol] = useState<number | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);

  // Clipboard
  const [clipboard, setClipboard] = useState<string | null>(null);

  // Refs
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track if user is actively editing (to avoid overwriting their changes)
  const isUserEditing = editingCell !== null;

  // Sync with sheet changes from store (e.g., AI edits, external changes)
  // Also syncs when switching sheets
  useEffect(() => {
    // Don't overwrite user's in-progress edits
    if (isUserEditing) return;

    setData(sheet.data);
    setFormats(sheet.formats);
    setColWidths(sheet.colWidths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet.id, sheet.updatedAt, isUserEditing]); // Intentionally only sync on id/updatedAt (external changes)

  // ─────────────────────────────────────────────────────────────────────────────
  // Cell Getter
  // ─────────────────────────────────────────────────────────────────────────────

  const getCell: CellGetter = useCallback(
    (row: number, col: number) => data[row]?.[col] || "",
    [data]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Display Value
  // ─────────────────────────────────────────────────────────────────────────────

  const getCellDisplay = useCallback(
    (row: number, col: number): string => {
      const value = data[row]?.[col] || "";
      if (!value) return "";

      let displayVal: string | number = value;
      if (value.startsWith("=")) {
        const result = evaluateFormula(value, getCell, new Set(), liveCache);
        displayVal = result.value;
      }

      const format = formats[`${row},${col}`];
      return formatDisplayValue(displayVal, format);
    },
    [data, getCell, liveCache, formats]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Save Functions
  // ─────────────────────────────────────────────────────────────────────────────

  const debouncedSaveData = useCallback(
    (newData: string[][]) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        onDataChange?.(newData);
      }, 500);
    },
    [onDataChange]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Live Data
  // ─────────────────────────────────────────────────────────────────────────────

  const fetchLiveData = useCallback(async () => {
    const liveFormulas = extractLiveFormulas(data);
    if (liveFormulas.length === 0) return;

    setIsLoadingLive(true);
    const newCache = new Map<string, number | null>();

    await Promise.all(
      liveFormulas.map(async ({ ticker, metric }) => {
        if (!ticker || !metric) return;
        const cacheKey = `${ticker}:${metric}`;
        if (newCache.has(cacheKey)) return;
        try {
          const value = await fetchLiveValue(ticker, metric);
          newCache.set(cacheKey, value);
        } catch {
          newCache.set(cacheKey, null);
        }
      })
    );

    setLiveCache(newCache);
    setIsLoadingLive(false);
  }, [data]);

  // Track live formula keys for refetch trigger
  const liveFormulaKeys = useMemo(() => {
    const formulas = extractLiveFormulas(data);
    return formulas.map((f) => `${f.ticker}:${f.metric}`).sort().join(",");
  }, [data]);

  // Fetch live data when keys change
  useEffect(() => {
    if (liveFormulaKeys) {
      fetchLiveData();
    }
  }, [liveFormulaKeys, fetchLiveData]);

  // Auto-refresh live data
  useEffect(() => {
    const liveFormulas = extractLiveFormulas(data);
    if (liveFormulas.length > 0) {
      liveRefreshIntervalRef.current = setInterval(fetchLiveData, 60000);
    }
    return () => {
      if (liveRefreshIntervalRef.current) {
        clearInterval(liveRefreshIntervalRef.current);
      }
    };
  }, [data, fetchLiveData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (liveRefreshIntervalRef.current) clearInterval(liveRefreshIntervalRef.current);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Cell Operations
  // ─────────────────────────────────────────────────────────────────────────────

  const commitEdit = useCallback((): string[][] => {
    if (!editingCell || readOnly) return data;

    const newData = data.map((row, ri) =>
      row.map((cell, ci) =>
        ri === editingCell.row && ci === editingCell.col ? editValue : cell
      )
    );
    setData(newData);
    debouncedSaveData(newData);

    if (containsLiveFormula(editValue)) {
      setTimeout(() => fetchLiveData(), 100);
    }

    return newData;
  }, [editingCell, editValue, data, debouncedSaveData, fetchLiveData, readOnly]);

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (editingCell && (editingCell.row !== row || editingCell.col !== col)) {
        commitEdit();
      }
      setSelectedCell({ row, col });
      setEditingCell(null);
    },
    [editingCell, commitEdit]
  );

  const handleCellDoubleClick = useCallback(
    (row: number, col: number) => {
      if (readOnly) return;
      setSelectedCell({ row, col });
      setEditingCell({ row, col });
      setEditValue(data[row]?.[col] || "");
    },
    [data, readOnly]
  );

  const startEditing = useCallback(() => {
    if (selectedCell && !editingCell && !readOnly) {
      setEditingCell(selectedCell);
      setEditValue(data[selectedCell.row]?.[selectedCell.col] || "");
    }
  }, [selectedCell, editingCell, data, readOnly]);

  const handleEditChange = useCallback((value: string) => {
    setEditValue(value);
  }, []);

  const handleEditBlur = useCallback(() => {
    if (editingCell) {
      commitEdit();
    }
    setEditingCell(null);
  }, [editingCell, commitEdit]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Keyboard Navigation
  // ─────────────────────────────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedCell) return;

      const { row, col } = selectedCell;
      const rows = sheet.rows;
      const cols = sheet.cols;

      if (editingCell) {
        if (e.key === "Enter") {
          e.preventDefault();
          commitEdit();
          setEditingCell(null);
          if (row < rows - 1) {
            setSelectedCell({ row: row + 1, col });
          }
        } else if (e.key === "Escape") {
          setEditingCell(null);
          setEditValue("");
        } else if (e.key === "Tab") {
          e.preventDefault();
          commitEdit();
          setEditingCell(null);
          if (col < cols - 1) {
            setSelectedCell({ row, col: col + 1 });
          } else if (row < rows - 1) {
            setSelectedCell({ row: row + 1, col: 0 });
          }
        }
        return;
      }

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          if (row > 0) setSelectedCell({ row: row - 1, col });
          break;
        case "ArrowDown":
          e.preventDefault();
          if (row < rows - 1) setSelectedCell({ row: row + 1, col });
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (col > 0) setSelectedCell({ row, col: col - 1 });
          break;
        case "ArrowRight":
          e.preventDefault();
          if (col < cols - 1) setSelectedCell({ row, col: col + 1 });
          break;
        case "Enter":
          e.preventDefault();
          startEditing();
          break;
        case "Tab":
          e.preventDefault();
          if (col < cols - 1) {
            setSelectedCell({ row, col: col + 1 });
          } else if (row < rows - 1) {
            setSelectedCell({ row: row + 1, col: 0 });
          }
          break;
        case "Delete":
        case "Backspace": {
          if (readOnly) break;
          e.preventDefault();
          const newData = data.map((r, ri) =>
            r.map((c, ci) => (ri === row && ci === col ? "" : c))
          );
          setData(newData);
          debouncedSaveData(newData);
          break;
        }
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !readOnly) {
            setEditingCell({ row, col });
            setEditValue(e.key);
          }
          break;
      }
    },
    [selectedCell, editingCell, sheet.rows, sheet.cols, data, commitEdit, startEditing, debouncedSaveData, readOnly]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Copy/Paste
  // ─────────────────────────────────────────────────────────────────────────────

  const copyCell = useCallback(() => {
    if (!selectedCell) return;
    const value = data[selectedCell.row]?.[selectedCell.col] || "";
    setClipboard(value);
    navigator.clipboard?.writeText(value);
  }, [selectedCell, data]);

  const pasteCell = useCallback(async () => {
    if (!selectedCell || readOnly) return;
    let value = clipboard;
    try {
      value = (await navigator.clipboard?.readText()) || clipboard;
    } catch {
      // Use internal clipboard
    }
    if (value === null) return;

    const newData = data.map((row, ri) =>
      row.map((cell, ci) =>
        ri === selectedCell.row && ci === selectedCell.col ? value! : cell
      )
    );
    setData(newData);
    debouncedSaveData(newData);
  }, [selectedCell, clipboard, data, debouncedSaveData, readOnly]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Formatting
  // ─────────────────────────────────────────────────────────────────────────────

  const setFormat = useCallback(
    (update: Partial<CellFormat>) => {
      if (!selectedCell || readOnly) return;
      const key = `${selectedCell.row},${selectedCell.col}`;
      const newFormats = {
        ...formats,
        [key]: { ...formats[key], ...update },
      };
      setFormats(newFormats);
      onFormatsChange?.(newFormats);
    },
    [selectedCell, formats, onFormatsChange, readOnly]
  );

  const currentFormat = selectedCell
    ? formats[`${selectedCell.row},${selectedCell.col}`]
    : undefined;

  // ─────────────────────────────────────────────────────────────────────────────
  // Column Resize
  // ─────────────────────────────────────────────────────────────────────────────

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, colIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      setResizingCol(colIdx);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = colWidths[colIdx] || DEFAULT_COL_WIDTH;
    },
    [colWidths]
  );

  useEffect(() => {
    if (resizingCol === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current;
      const newWidth = Math.max(MIN_COL_WIDTH, resizeStartWidth.current + delta);
      const newColWidths = [...colWidths];
      newColWidths[resizingCol] = newWidth;
      setColWidths(newColWidths);
    };

    const handleMouseUp = () => {
      onColWidthsChange?.(colWidths);
      setResizingCol(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizingCol, colWidths, onColWidthsChange]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Fill Drag
  // ─────────────────────────────────────────────────────────────────────────────

  const handleFillDragStart = useCallback(
    (e: React.MouseEvent, cell: CellPosition) => {
      if (readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      setFillDrag({
        startCell: cell,
        endCell: cell,
        direction: null,
      });
      setFillPreviewCells([]);
    },
    [readOnly]
  );

  const handleFillDragMove = useCallback(
    (targetCell: CellPosition) => {
      if (!fillDrag || !selectedCell) return;

      const startCell = fillDrag.startCell;
      const rowDiff = targetCell.row - startCell.row;
      const colDiff = targetCell.col - startCell.col;

      let direction: FillDragState["direction"] = null;
      const cells: CellPosition[] = [];

      if (Math.abs(rowDiff) >= Math.abs(colDiff)) {
        if (rowDiff > 0) {
          direction = "down";
          for (let r = startCell.row + 1; r <= targetCell.row; r++) {
            cells.push({ row: r, col: startCell.col });
          }
        } else if (rowDiff < 0) {
          direction = "up";
          for (let r = startCell.row - 1; r >= targetCell.row; r--) {
            cells.push({ row: r, col: startCell.col });
          }
        }
      } else {
        if (colDiff > 0) {
          direction = "right";
          for (let c = startCell.col + 1; c <= targetCell.col; c++) {
            cells.push({ row: startCell.row, col: c });
          }
        } else if (colDiff < 0) {
          direction = "left";
          for (let c = startCell.col - 1; c >= targetCell.col; c--) {
            cells.push({ row: startCell.row, col: c });
          }
        }
      }

      setFillDrag({
        ...fillDrag,
        endCell: targetCell,
        direction,
      });
      setFillPreviewCells(cells);
    },
    [fillDrag, selectedCell]
  );

  const handleFillDragEnd = useCallback(() => {
    if (!fillDrag || !selectedCell || fillPreviewCells.length === 0) {
      setFillDrag(null);
      setFillPreviewCells([]);
      return;
    }

    const sourceCell = fillDrag.startCell;
    const sourceValue = data[sourceCell.row]?.[sourceCell.col] || "";
    const sourceFormat = formats[`${sourceCell.row},${sourceCell.col}`];

    const newData = data.map((row) => [...row]);
    const newFormats = { ...formats };

    fillPreviewCells.forEach((cell) => {
      const rowDelta = cell.row - sourceCell.row;
      const colDelta = cell.col - sourceCell.col;

      let newValue = sourceValue;
      if (sourceValue.startsWith("=")) {
        newValue = adjustFormulaCellRefs(sourceValue, rowDelta, colDelta);
      }

      const targetRow = newData[cell.row];
      if (targetRow) {
        targetRow[cell.col] = newValue;
      }

      if (sourceFormat) {
        newFormats[`${cell.row},${cell.col}`] = { ...sourceFormat };
      }
    });

    setData(newData);
    setFormats(newFormats);
    debouncedSaveData(newData);
    onFormatsChange?.(newFormats);

    if (containsLiveFormula(sourceValue)) {
      setTimeout(() => fetchLiveData(), 100);
    }

    setFillDrag(null);
    setFillPreviewCells([]);
  }, [fillDrag, selectedCell, fillPreviewCells, data, formats, debouncedSaveData, onFormatsChange, fetchLiveData]);

  // Global mouse up for fill drag
  useEffect(() => {
    if (!fillDrag) return;

    const handleMouseUp = () => {
      handleFillDragEnd();
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [fillDrag, handleFillDragEnd]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Stats
  // ─────────────────────────────────────────────────────────────────────────────

  const liveCellCount = useMemo(
    () => extractLiveFormulas(data).length,
    [data]
  );

  const selectedValue = selectedCell
    ? data[selectedCell.row]?.[selectedCell.col]
    : null;
  const selectedIsFormula = selectedValue?.startsWith("=");

  // ─────────────────────────────────────────────────────────────────────────────
  // Return API
  // ─────────────────────────────────────────────────────────────────────────────

  return {
    // Data
    data,
    formats,
    colWidths,
    rows: sheet.rows,
    cols: sheet.cols,

    // Selection
    selectedCell,
    setSelectedCell,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    currentFormat,

    // Live data
    liveCache,
    isLoadingLive,
    liveCellCount,
    fetchLiveData,

    // Fill drag
    fillDrag,
    fillPreviewCells,

    // Display
    getCellDisplay,

    // Cell operations
    handleCellClick,
    handleCellDoubleClick,
    handleEditChange,
    handleEditBlur,
    handleKeyDown,
    startEditing,
    commitEdit,

    // Copy/paste
    copyCell,
    pasteCell,

    // Formatting
    setFormat,

    // Resize
    handleResizeStart,
    resizingCol,

    // Fill drag
    handleFillDragStart,
    handleFillDragMove,
    handleFillDragEnd,

    // Stats
    selectedIsFormula,
    selectedValue,
  };
}
